<template>
  <div class="cfb">
    <!-- ESTADO VAZIO — caixa ilustrada + presets em pill + link "navegar todos" -->
    <div v-if="rules.length === 0" class="cfb-empty">
      <div class="cfb-empty-icon">
        <i class="bx bx-filter"></i>
      </div>
      <h3 class="cfb-empty-title">Filtre seus participantes</h3>
      <p class="cfb-empty-desc">Combine filtros pra escolher quem recebe a campanha. Comece pelos mais usados:</p>
      <div class="cfb-presets">
        <button
          v-for="f in presetFields"
          :key="f.key"
          type="button"
          class="cfb-preset-pill"
          :class="`tone-${keyTone(f.key, f._category)}`"
          :disabled="loading"
          @click="addRule(f, f._category)"
        >
          <span class="cfb-preset-pill-icon">
            <i :class="`bx ${componentIcon(f.component)}`"></i>
          </span>
          {{ f.label }}
          <i class="bx bx-plus cfb-preset-pill-plus"></i>
        </button>
      </div>
      <button type="button" class="cfb-empty-link" @click="openPickerInline">
        Ou navegue por todos os campos
      </button>
    </div>

    <!-- LISTA DE RULES + BOTÃO ADICIONAR -->
    <div v-else class="cfb-rules">
      <transition-group name="cfb-row" tag="div" class="cfb-rules-list">
        <div
          v-for="(rule, idx) in rules"
          :key="rule.key + ':' + idx"
          class="cfb-row"
        >
          <!-- coluna ONDE / E -->
          <div class="cfb-row-conn">{{ idx === 0 ? 'onde' : 'e' }}</div>

          <!-- coluna ícone tonal + label -->
          <div class="cfb-row-field">
            <span class="cfb-row-icon" :class="`tone-${keyTone(rule.key, rule._category)}`">
              <i :class="`bx ${componentIcon(rule.component)}`"></i>
            </span>
            <span class="cfb-row-label">{{ rule._fieldLabel || rule.label }}</span>
          </div>

          <!-- coluna input -->
          <div class="cfb-row-input">
            <!-- TEXT -->
            <input
              v-if="rule.component === 'text'"
              type="text"
              :value="rule.value || ''"
              :placeholder="`Digite o valor…`"
              class="cfb-input"
              @input="onTextInput(idx, $event.target.value)"
            />

            <!-- SELECT (single) -->
            <div
              v-else-if="rule.component === 'select'"
              class="cfb-select-wrap"
              v-click-outside="() => closeSelect(idx)"
            >
              <button
                type="button"
                class="cfb-select-trigger"
                :class="{ 'is-open': selectOpen[idx] }"
                @click="toggleSelect(idx)"
              >
                <span class="cfb-select-text" :class="{ 'is-placeholder': !rule.value }">
                  {{ singleSelectDisplay(rule) || 'Selecionar…' }}
                </span>
                <i class="bx bx-chevron-down cfb-select-chevron"></i>
              </button>
              <div v-if="selectOpen[idx]" class="cfb-select-menu">
                <button
                  v-for="opt in optionsFor(rule)"
                  :key="String(opt.value !== undefined ? opt.value : opt.id)"
                  type="button"
                  class="cfb-select-option"
                  :class="{ 'is-checked': isSingleChecked(rule, opt) }"
                  @click="onSelectChange(idx, opt)"
                >
                  <span class="cfb-radio" :class="{ 'is-checked': isSingleChecked(rule, opt) }">
                    <i v-if="isSingleChecked(rule, opt)" class="bx bx-check"></i>
                  </span>
                  {{ opt.label || opt.name }}
                </button>
              </div>
            </div>

            <!-- MULTISELECT -->
            <div
              v-else-if="rule.component === 'multiselect'"
              class="cfb-select-wrap"
              v-click-outside="() => closeSelect(idx)"
            >
              <button
                type="button"
                class="cfb-select-trigger"
                :class="{ 'is-open': selectOpen[idx] }"
                @click="toggleSelect(idx)"
              >
                <span class="cfb-select-text" :class="{ 'is-placeholder': multiSelectEmpty(rule) }">
                  {{ multiSelectDisplay(rule) || 'Selecionar…' }}
                </span>
                <i class="bx bx-chevron-down cfb-select-chevron"></i>
              </button>
              <div v-if="selectOpen[idx]" class="cfb-select-menu cfb-thin-scroll">
                <button
                  v-for="opt in optionsFor(rule)"
                  :key="String(opt.value !== undefined ? opt.value : opt.id)"
                  type="button"
                  class="cfb-select-option"
                  :class="{ 'is-checked': isMultiChecked(rule, opt) }"
                  @click="toggleMulti(idx, opt)"
                >
                  <span class="cfb-checkbox" :class="{ 'is-checked': isMultiChecked(rule, opt) }">
                    <i v-if="isMultiChecked(rule, opt)" class="bx bx-check"></i>
                  </span>
                  {{ opt.label || opt.name }}
                </button>
              </div>
            </div>

            <!-- DATE RANGE -->
            <div v-else-if="rule.component === 'date-range'" class="cfb-date-range">
              <input
                type="date"
                :value="(rule.value && rule.value.start) || ''"
                class="cfb-input cfb-date-input"
                @input="onDateChange(idx, 'start', $event.target.value)"
              />
              <span class="cfb-date-sep">→</span>
              <input
                type="date"
                :value="(rule.value && rule.value.end) || ''"
                class="cfb-input cfb-date-input"
                @input="onDateChange(idx, 'end', $event.target.value)"
              />
            </div>
          </div>

          <!-- coluna remover -->
          <button
            type="button"
            class="cfb-row-remove"
            aria-label="Remover filtro"
            @click="removeRule(idx)"
          >
            <i class="bx bx-x"></i>
          </button>
        </div>
      </transition-group>

      <!-- BOTÃO ADICIONAR FILTRO + popover -->
      <div class="cfb-add-wrap" v-click-outside="closePicker">
        <button
          type="button"
          class="cfb-add-btn"
          :class="{ 'is-open': pickerOpen }"
          :disabled="loading"
          @click="togglePicker"
        >
          <i class="bx bx-plus"></i>
          Adicionar filtro
        </button>

        <!-- FIELD PICKER POPOVER -->
        <div v-if="pickerOpen" class="cfb-picker">
          <div class="cfb-picker-header">
            <i class="bx bx-search cfb-picker-search-icon"></i>
            <input
              ref="pickerSearch"
              v-model="pickerQuery"
              type="text"
              placeholder="Buscar campo…"
              class="cfb-picker-search"
              @keydown.esc="closePicker"
            />
            <kbd class="cfb-picker-kbd">esc</kbd>
          </div>
          <div class="cfb-picker-body cfb-thin-scroll">
            <div v-if="filteredPickerGroups.length === 0" class="cfb-picker-empty">
              Nenhum campo encontrado
            </div>
            <div
              v-for="group in filteredPickerGroups"
              :key="group.category"
              class="cfb-picker-group"
            >
              <div class="cfb-picker-group-header">
                <i :class="`bx ${pickerCategoryIcon(group.category)}`"></i>
                {{ group.label }}
              </div>
              <button
                v-for="f in group.fields"
                :key="f.key"
                type="button"
                class="cfb-picker-item"
                :class="`tone-${keyTone(f.key, group.category)}`"
                @click="addRule(f, group.category)"
              >
                <span class="cfb-picker-item-icon">
                  <i :class="`bx ${componentIcon(f.component)}`"></i>
                </span>
                <span class="cfb-picker-item-text">
                  <span class="cfb-picker-item-label">{{ f.label }}</span>
                  <span class="cfb-picker-item-helper">{{ helperFor(f.component) }}</span>
                </span>
                <i class="bx bx-right-arrow-alt cfb-picker-item-arrow"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { states } from '@/pages/participants/statesData'

const PRESET_KEYS = ['status', 'group_id', 'tags', 'status_check', 'ref_status']

// Per-key tone overrides — alinhado com a ref (status=blue, tags=purple, etc)
function keyTone (key, category) {
  if (key === 'status' || key === 'ref_status') return 'blue'
  if (key === 'tags' || (key && key.endsWith('_tag_bundle'))) return 'purple'
  if (key === 'status_check') return 'teal'
  if (key === 'group_id') return 'indigo'
  if (category && category.startsWith('rc_')) return 'amber'
  if (category && category.startsWith('form_')) return 'gray'
  return 'gray'
}

// Iconify por tipo de input
function componentIcon (component) {
  switch (component) {
    case 'text': return 'bx-text'
    case 'select': return 'bx-list-ul'
    case 'multiselect': return 'bx-purchase-tag'
    case 'date-range': return 'bx-calendar'
    default: return 'bx-filter-alt'
  }
}

// Diretiva inline pra fechar selects/picker ao clicar fora
const clickOutside = {
  bind (el, binding) {
    el._cfbClickOutside = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event)
      }
    }
    document.addEventListener('mousedown', el._cfbClickOutside)
  },
  unbind (el) {
    document.removeEventListener('mousedown', el._cfbClickOutside)
  }
}

export default {
  name: 'CampaignFilterBuilder',
  directives: { clickOutside },
  props: {
    value: { type: Array, default: () => [] },
    catalog: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },
  data () {
    return {
      pickerOpen: false,
      pickerQuery: '',
      // mapa idx -> bool, controla open de cada select inline
      selectOpen: {},
      statesList: states || []
    }
  },
  computed: {
    ...mapState('tag', ['tags']),
    // statusSelect: array de {id, name} carregado por GetStatusSelect
    // refstatusList: objeto {id: name}
    ...mapState('settings', ['statusSelect', 'refstatusList']),
    ...mapState('group', ['groups']),

    rules () {
      return this.value || []
    },

    catalogByKey () {
      const map = {}
      this.catalog.forEach(cat => {
        (cat.fields || []).forEach(f => { map[f.key] = f })
      })
      return map
    },

    usedKeys () {
      return this.rules.map(r => r.key)
    },

    // 5 presets resolvidos do catálogo (na ordem definida em PRESET_KEYS)
    presetFields () {
      const out = []
      for (const k of PRESET_KEYS) {
        const found = this.findFieldByKey(k)
        if (found) out.push({ ...found.field, _category: found.category })
      }
      return out
    },

    // Catálogo filtrado pelo picker (search + remove já usados + respeita show_when)
    filteredPickerGroups () {
      const q = (this.pickerQuery || '').toLowerCase().trim()
      return this.catalog
        .map(cat => ({
          category: cat.category,
          label: cat.label,
          fields: (cat.fields || [])
            .filter(f => !this.usedKeys.includes(f.key))
            .filter(f => this.passesShowWhen(f))
            .filter(f => !q || (f.label || '').toLowerCase().includes(q))
        }))
        .filter(cat => cat.fields.length > 0)
    }
  },
  methods: {
    keyTone,
    componentIcon,

    findFieldByKey (key) {
      for (const cat of this.catalog) {
        for (const f of (cat.fields || [])) {
          if (f.key === key) return { field: f, category: cat.category }
        }
      }
      return null
    },

    passesShowWhen (field) {
      if (!field.show_when) return true
      const dep = this.rules.find(r => r.key === field.show_when.field)
      if (!dep) return false
      const v = dep.value
      if (Array.isArray(v)) return v.map(String).includes(String(field.show_when.is))
      return String(v) === String(field.show_when.is)
    },

    helperFor (component) {
      switch (component) {
        case 'text': return 'Texto livre'
        case 'select': return 'Escolha única'
        case 'multiselect': return 'Múltipla seleção'
        case 'date-range': return 'Intervalo de datas'
        default: return ''
      }
    },

    pickerCategoryIcon (category) {
      if (category === 'standard') return 'bx-user'
      if (category && category.startsWith('form_')) return 'bx-edit'
      if (category && category.startsWith('rc_')) return 'bx-buildings'
      return 'bx-grid-alt'
    },

    optionsFor (rule) {
      if (rule.static_options) return rule.static_options
      if (rule.key === 'status') {
        // statusSelect já vem como [{id, name}] do backend (inclui Convidado=99)
        return (this.statusSelect || []).map(s => ({
          id: s.id,
          name: s.name,
          value: s.id,
          label: s.name
        }))
      }
      if (rule.key === 'ref_status') {
        const obj = this.refstatusList || {}
        return Object.keys(obj).map(k => ({ value: k, label: obj[k], id: k, name: obj[k] }))
      }
      if (rule.key === 'group_id' || rule.key === 'companion_of_group') {
        return (this.groups || []).map(g => ({ id: g.id, name: g.name, value: g.id, label: g.name }))
      }
      if (rule.key === 'tags') {
        return (this.tags || []).map(t => ({ id: t.id, name: t.name, value: t.id, label: t.name }))
      }
      if (rule.source === 'static:states') {
        return this.statesList.map(s => ({ value: s.sigla || s.value, label: s.value || s.label, id: s.sigla || s.value, name: s.value || s.label }))
      }
      return []
    },

    // — SELECT helpers —
    toggleSelect (idx) {
      const cur = !!this.selectOpen[idx]
      // fecha todos antes (só um aberto por vez)
      this.selectOpen = {}
      if (!cur) this.$set(this.selectOpen, idx, true)
    },
    closeSelect (idx) {
      if (this.selectOpen[idx]) this.$set(this.selectOpen, idx, false)
    },
    isSingleChecked (rule, opt) {
      const v = rule.value
      const id = String(opt.value !== undefined ? opt.value : opt.id)
      return v != null && String(v) === id
    },
    isMultiChecked (rule, opt) {
      const v = rule.value
      const id = String(opt.value !== undefined ? opt.value : opt.id)
      return Array.isArray(v) && v.map(String).includes(id)
    },
    singleSelectDisplay (rule) {
      const v = rule.value
      if (v == null || v === '') return ''
      const opts = this.optionsFor(rule)
      const f = opts.find(o => String(o.value !== undefined ? o.value : o.id) === String(v))
      return f ? (f.label || f.name) : v
    },
    multiSelectEmpty (rule) {
      return !Array.isArray(rule.value) || rule.value.length === 0
    },
    multiSelectDisplay (rule) {
      const v = rule.value
      if (!Array.isArray(v) || v.length === 0) return ''
      const opts = this.optionsFor(rule)
      const names = v.map(id => {
        const f = opts.find(o => String(o.value !== undefined ? o.value : o.id) === String(id))
        return f ? (f.label || f.name) : id
      })
      if (names.length <= 2) return names.join(', ')
      return `${names[0]} +${names.length - 1}`
    },

    // — Picker —
    openPickerInline () {
      this.pickerOpen = true
      this.pickerQuery = ''
      this.$nextTick(() => { if (this.$refs.pickerSearch) this.$refs.pickerSearch.focus() })
    },
    togglePicker () {
      if (this.pickerOpen) {
        this.closePicker()
      } else {
        this.openPickerInline()
      }
    },
    closePicker () {
      this.pickerOpen = false
      this.pickerQuery = ''
    },

    // — Mutations —
    addRule (field, category) {
      let initialValue = null
      if (field.component === 'multiselect') initialValue = []
      else if (field.component === 'date-range') initialValue = { start: null, end: null }
      const rule = {
        key: field.key,
        query_rule: field.query_rule,
        component: field.component,
        multiple: !!field.multiple,
        source: field.source || null,
        static_options: field.static_options || null,
        show_when: field.show_when || null,
        value: initialValue,
        label: field.label,
        _fieldLabel: field.label,
        _category: category
      }
      this.emitRules([...this.rules, rule])
      this.closePicker()
    },
    removeRule (idx) {
      const next = this.rules.slice()
      next.splice(idx, 1)
      this.emitRules(next)
      this.closeSelect(idx)
    },
    onTextInput (idx, val) {
      const next = this.patchRule(idx, { value: val })
      next[idx].label = this.buildLabel(next[idx])
      this.emitRules(next)
    },
    onSelectChange (idx, opt) {
      const id = opt.value !== undefined ? opt.value : opt.id
      const next = this.patchRule(idx, { value: id })
      next[idx].label = this.buildLabel(next[idx])
      this.emitRules(next)
      this.closeSelect(idx)
    },
    toggleMulti (idx, opt) {
      const id = opt.value !== undefined ? opt.value : opt.id
      const cur = this.rules[idx].value
      const arr = Array.isArray(cur) ? cur.slice() : []
      const sid = String(id)
      const ix = arr.findIndex(x => String(x) === sid)
      if (ix >= 0) arr.splice(ix, 1)
      else arr.push(id)
      const next = this.patchRule(idx, { value: arr })
      next[idx].label = this.buildLabel(next[idx])
      this.emitRules(next)
    },
    onDateChange (idx, which, val) {
      const cur = this.rules[idx].value || { start: null, end: null }
      const newVal = { ...cur, [which]: val || null }
      const next = this.patchRule(idx, { value: newVal })
      next[idx].label = this.buildLabel(next[idx])
      this.emitRules(next)
    },
    patchRule (idx, patch) {
      const next = this.rules.slice()
      next[idx] = { ...next[idx], ...patch }
      return next
    },
    emitRules (rules) {
      this.$emit('input', rules)
    },

    // — Label resolver (mantém legado pra exibição em outros lugares) —
    buildLabel (rule) {
      const base = rule._fieldLabel || rule.label || rule.key
      const v = rule.value
      if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return base
      if (rule.component === 'text') return `${base}: ${v}`
      if (rule.component === 'select') {
        return `${base}: ${this.singleSelectDisplay(rule)}`
      }
      if (rule.component === 'multiselect') {
        return `${base}: ${this.multiSelectDisplay(rule)}`
      }
      if (rule.component === 'date-range') {
        const a = v.start
        const b = v.end
        if (a && b) return `${base}: ${a} → ${b}`
        if (a) return `${base}: a partir de ${a}`
        if (b) return `${base}: até ${b}`
        return base
      }
      return base
    }
  }
}
</script>

<style lang="scss" scoped>
/* ────────────────────────────────────────────────────────────────────
   Design tokens — copiados do design ref, com --focus alinhado ao
   primary do projeto (#556ee6) e bordas levemente mais sutis.
   Os tons de categoria mantêm o esquema da ref (chroma+lightness fixos,
   só varia hue) pra coerência visual.
   ──────────────────────────────────────────────────────────────────── */
.cfb {
  --cfb-bg:           #fafbfc;
  --cfb-surface:      #ffffff;
  --cfb-surface-2:    #f7f8fa;
  --cfb-surface-3:    #f1f3f6;
  --cfb-surface-hover: #ebeef2;

  --cfb-fg:           #2a2f3c;
  --cfb-fg-2:         #555b6a;
  --cfb-fg-3:         #7c8290;
  --cfb-fg-4:         #a4a9b3;

  --cfb-border:       #e7e9ee;
  --cfb-border-2:     #dadde3;
  --cfb-border-strong: #c2c6cf;

  --cfb-focus:        #556ee6;
  --cfb-focus-ring:   rgba(85, 110, 230, 0.18);

  /* Tons de categoria — uniformes em chroma, só hue varia */
  --tone-blue:    #4f6fe0;
  --tone-blue-bg: #eef1fc;
  --tone-blue-bd: #d6def8;

  --tone-purple:    #8a4fd0;
  --tone-purple-bg: #f4ecfb;
  --tone-purple-bd: #e4d3f3;

  --tone-indigo:    #5e54d6;
  --tone-indigo-bg: #ecebfa;
  --tone-indigo-bd: #d6d3f1;

  --tone-teal:    #2c9aa3;
  --tone-teal-bg: #e6f5f6;
  --tone-teal-bd: #cae8eb;

  --tone-amber:    #b97817;
  --tone-amber-bg: #fbf2e0;
  --tone-amber-bd: #f1e0b3;

  --tone-gray:    #6b7280;
  --tone-gray-bg: #f1f3f6;
  --tone-gray-bd: #e0e3e9;

  --r-sm: 6px;
  --r-md: 8px;
  --r-lg: 12px;
  --r-xl: 16px;
  --r-pill: 999px;

  --shadow-sm: 0 1px 2px rgba(15, 20, 35, 0.04);
  --shadow-md: 0 4px 12px rgba(15, 20, 35, 0.06), 0 1px 3px rgba(15, 20, 35, 0.04);
  --shadow-lg: 0 12px 32px rgba(15, 20, 35, 0.10), 0 2px 6px rgba(15, 20, 35, 0.04);
  --shadow-pop: 0 8px 24px rgba(15, 20, 35, 0.10), 0 2px 4px rgba(15, 20, 35, 0.04), 0 0 0 1px var(--cfb-border);

  font-family: inherit;
  color: var(--cfb-fg);
  font-size: 13px;
}

/* Scrollbar fina (mesmo padrão das notificações do participante) */
.cfb-thin-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
.cfb-thin-scroll::-webkit-scrollbar-thumb { background: var(--cfb-border-2); border-radius: 4px; }
.cfb-thin-scroll::-webkit-scrollbar-track { background: transparent; }

/* ────────────────────────────────────────────────────────────────────
   Estado vazio
   ──────────────────────────────────────────────────────────────────── */
.cfb-empty {
  padding: 36px 24px 32px;
  text-align: center;
  background: var(--cfb-bg);
  border: 1px solid var(--cfb-border);
  border-radius: var(--r-lg);
}
.cfb-empty-icon {
  margin: 0 auto 14px;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--cfb-surface-3);
  border: 1px solid var(--cfb-border);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--cfb-fg-2);
  i { font-size: 22px; line-height: 1; }
}
.cfb-empty-title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 600;
  color: var(--cfb-fg);
}
.cfb-empty-desc {
  margin: 0 auto 20px;
  font-size: 13px;
  color: var(--cfb-fg-3);
  max-width: 380px;
  line-height: 1.5;
}
.cfb-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  margin-bottom: 14px;
}
.cfb-preset-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 6px 8px;
  background: var(--cfb-surface);
  border: 1px solid var(--cfb-border);
  border-radius: var(--r-pill);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--cfb-fg-2);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s, transform .12s;
  font-family: inherit;
  animation: pillEnter .22s cubic-bezier(.2,.7,.3,1);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }

  // Tons no hover — pega da classe .tone-X aplicada
  @each $tone in (blue, purple, indigo, teal, amber, gray) {
    &.tone-#{$tone}:hover:not(:disabled) {
      background: var(--tone-#{$tone}-bg);
      border-color: var(--tone-#{$tone}-bd);
      color: var(--tone-#{$tone});
    }
  }
}
.cfb-preset-pill-icon {
  width: 18px; height: 18px; border-radius: 4px;
  background: var(--cfb-surface-3);
  display: inline-flex; align-items: center; justify-content: center;
  i { font-size: 11px; line-height: 1; }
  // Em cada tone, o ícone fica destacado
  .cfb-preset-pill.tone-blue   & { background: var(--tone-blue-bg);   color: var(--tone-blue); }
  .cfb-preset-pill.tone-purple & { background: var(--tone-purple-bg); color: var(--tone-purple); }
  .cfb-preset-pill.tone-indigo & { background: var(--tone-indigo-bg); color: var(--tone-indigo); }
  .cfb-preset-pill.tone-teal   & { background: var(--tone-teal-bg);   color: var(--tone-teal); }
  .cfb-preset-pill.tone-amber  & { background: var(--tone-amber-bg);  color: var(--tone-amber); }
  .cfb-preset-pill.tone-gray   & { background: var(--tone-gray-bg);   color: var(--tone-gray); }
}
.cfb-preset-pill-plus { font-size: 12px; }
.cfb-empty-link {
  background: transparent;
  border: none;
  color: var(--cfb-fg-3);
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-family: inherit;
  &:hover { color: var(--cfb-fg-2); }
}

/* ────────────────────────────────────────────────────────────────────
   Lista de rules
   ──────────────────────────────────────────────────────────────────── */
.cfb-rules {
  background: var(--cfb-bg);
  border: 1px solid var(--cfb-border);
  border-radius: var(--r-lg);
  padding: 12px;
}
.cfb-rules-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
}

/* Transition group anims */
.cfb-row-enter-active { animation: rowEnter .25s cubic-bezier(.2,.7,.3,1); }
.cfb-row-leave-active {
  transition: opacity .22s, transform .22s;
  position: absolute;
  width: calc(100% - 24px);
}
.cfb-row-leave-to { opacity: 0; transform: translateX(8px); }

.cfb-row {
  display: grid;
  grid-template-columns: 36px minmax(170px, 1fr) minmax(220px, 1.4fr) 28px;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: var(--cfb-surface);
  border: 1px solid var(--cfb-border);
  border-radius: var(--r-md);
  transition: background .15s, border-color .15s;

  &:hover { border-color: var(--cfb-border-2); }
}
.cfb-row-conn {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: var(--cfb-fg-4);
  text-transform: uppercase;
  text-align: center;
}
.cfb-row-field {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.cfb-row-icon {
  width: 22px; height: 22px; border-radius: 5px;
  display: inline-flex; align-items: center; justify-content: center;
  flex: 0 0 auto;
  i { font-size: 12px; line-height: 1; }
  @each $tone in (blue, purple, indigo, teal, amber, gray) {
    &.tone-#{$tone} {
      background: var(--tone-#{$tone}-bg);
      border: 1px solid var(--tone-#{$tone}-bd);
      color: var(--tone-#{$tone});
    }
  }
}
.cfb-row-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--cfb-fg);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cfb-row-input { min-width: 0; }
.cfb-row-remove {
  width: 28px; height: 28px; border-radius: 6px;
  border: none; background: transparent; cursor: pointer;
  color: var(--cfb-fg-3);
  display: inline-flex; align-items: center; justify-content: center;
  transition: background .12s, color .12s;
  i { font-size: 16px; line-height: 1; }
  &:hover { background: var(--cfb-surface-3); color: var(--cfb-fg); }
}

/* ────────────────────────────────────────────────────────────────────
   Inputs (text, select, multiselect, date-range)
   ──────────────────────────────────────────────────────────────────── */
.cfb-input {
  width: 100%;
  padding: 7px 10px;
  font-size: 13px;
  background: var(--cfb-surface);
  border: 1px solid var(--cfb-border);
  border-radius: var(--r-md);
  color: var(--cfb-fg);
  outline: none;
  font-family: inherit;
  transition: border-color .15s, box-shadow .15s;

  &:focus {
    border-color: var(--cfb-focus);
    box-shadow: 0 0 0 3px var(--cfb-focus-ring);
  }
  &::placeholder { color: var(--cfb-fg-3); }
}
.cfb-date-range {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}
.cfb-date-input { flex: 1; min-width: 0; }
.cfb-date-sep { color: var(--cfb-fg-3); font-size: 12px; }

.cfb-select-wrap { position: relative; width: 100%; }
.cfb-select-trigger {
  width: 100%;
  text-align: left;
  padding: 7px 10px;
  font-size: 13px;
  background: var(--cfb-surface);
  border: 1px solid var(--cfb-border);
  border-radius: var(--r-md);
  cursor: pointer;
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  transition: border-color .15s, box-shadow .15s;
  outline: none;
  font-family: inherit;
  color: var(--cfb-fg);

  &.is-open {
    border-color: var(--cfb-focus);
    box-shadow: 0 0 0 3px var(--cfb-focus-ring);
  }
}
.cfb-select-text {
  display: block;
  flex: 1;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  &.is-placeholder { color: var(--cfb-fg-3); }
}
.cfb-select-chevron { font-size: 14px; color: var(--cfb-fg-3); flex: 0 0 auto; }
.cfb-select-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0; right: 0;
  z-index: 30;
  background: var(--cfb-surface);
  border: 1px solid var(--cfb-border);
  border-radius: var(--r-md);
  box-shadow: var(--shadow-pop);
  max-height: 240px;
  overflow-y: auto;
  padding: 4px;
  animation: fadeSlide .14s ease-out;
}
.cfb-select-option {
  display: flex; align-items: center; gap: 8px;
  width: 100%; text-align: left;
  padding: 6px 8px;
  background: transparent;
  border: none; border-radius: var(--r-sm);
  font-size: 13px; color: var(--cfb-fg);
  cursor: pointer;
  font-family: inherit;
  transition: background .1s;

  &:hover { background: var(--cfb-surface-2); }
  &.is-checked {
    background: var(--cfb-surface-3);
    &:hover { background: var(--cfb-surface-3); }
  }
}
.cfb-checkbox, .cfb-radio {
  width: 14px; height: 14px;
  border: 1.4px solid var(--cfb-border-strong);
  background: transparent;
  display: inline-flex; align-items: center; justify-content: center;
  flex: 0 0 auto;
  transition: background .12s, border-color .12s;
  i { font-size: 10px; color: #fff; line-height: 1; font-weight: 700; }
  &.is-checked {
    background: var(--cfb-focus);
    border-color: var(--cfb-focus);
  }
}
.cfb-checkbox { border-radius: 3px; }
.cfb-radio { border-radius: 7px; }

/* ────────────────────────────────────────────────────────────────────
   Botão + popover de adicionar filtro
   ──────────────────────────────────────────────────────────────────── */
.cfb-add-wrap {
  position: relative;
  margin-top: 4px;
  display: inline-block;
}
.cfb-add-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 12px;
  background: transparent;
  border: 1px dashed var(--cfb-border-2);
  color: var(--cfb-fg-2);
  border-radius: var(--r-md);
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
  font-family: inherit;
  i { font-size: 13px; }
  &:hover:not(:disabled), &.is-open {
    background: var(--cfb-surface-3);
    border-color: var(--cfb-border-strong);
    color: var(--cfb-fg);
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

.cfb-picker {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 360px;
  background: var(--cfb-surface);
  border: 1px solid var(--cfb-border);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-lg);
  z-index: 50;
  overflow: hidden;
  animation: fadeSlide .14s ease-out;
}
.cfb-picker-header {
  padding: 10px;
  border-bottom: 1px solid var(--cfb-border);
  display: flex;
  align-items: center;
  gap: 8px;
}
.cfb-picker-search-icon {
  font-size: 15px;
  color: var(--cfb-fg-3);
}
.cfb-picker-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: var(--cfb-fg);
  padding: 0;
  font-family: inherit;
  &::placeholder { color: var(--cfb-fg-3); }
}
.cfb-picker-kbd {
  font-size: 10px;
  font-family: ui-monospace, "JetBrains Mono", "SF Mono", Menlo, monospace;
  color: var(--cfb-fg-3);
  padding: 1px 5px;
  border: 1px solid var(--cfb-border);
  border-radius: 4px;
  background: var(--cfb-surface-2);
}
.cfb-picker-body {
  max-height: 360px;
  overflow-y: auto;
  padding: 4px;
}
.cfb-picker-empty {
  padding: 24px 12px;
  text-align: center;
  color: var(--cfb-fg-3);
  font-size: 13px;
}
.cfb-picker-group { padding: 4px 0 6px; }
.cfb-picker-group-header {
  position: sticky;
  top: 0;
  padding: 8px 10px 4px;
  background: var(--cfb-surface);
  font-size: 11px;
  font-weight: 600;
  color: var(--cfb-fg-3);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 1;
  i { font-size: 12px; }
}
.cfb-picker-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: var(--r-sm);
  cursor: pointer;
  transition: background .12s;
  font-family: inherit;
  color: inherit;

  @each $tone in (blue, purple, indigo, teal, amber, gray) {
    &.tone-#{$tone}:hover { background: var(--tone-#{$tone}-bg); }
    &.tone-#{$tone}:hover .cfb-picker-item-arrow { color: var(--tone-#{$tone}); }
  }
}
.cfb-picker-item-icon {
  width: 26px; height: 26px; border-radius: 6px;
  display: inline-flex; align-items: center; justify-content: center;
  flex: 0 0 auto;
  background: var(--cfb-surface-3);
  color: var(--cfb-fg-2);
  i { font-size: 14px; line-height: 1; }
  // Cada item recebe a cor do seu tone
  .cfb-picker-item.tone-blue   & { background: var(--tone-blue-bg);   color: var(--tone-blue);   border: 1px solid var(--tone-blue-bd); }
  .cfb-picker-item.tone-purple & { background: var(--tone-purple-bg); color: var(--tone-purple); border: 1px solid var(--tone-purple-bd); }
  .cfb-picker-item.tone-indigo & { background: var(--tone-indigo-bg); color: var(--tone-indigo); border: 1px solid var(--tone-indigo-bd); }
  .cfb-picker-item.tone-teal   & { background: var(--tone-teal-bg);   color: var(--tone-teal);   border: 1px solid var(--tone-teal-bd); }
  .cfb-picker-item.tone-amber  & { background: var(--tone-amber-bg);  color: var(--tone-amber);  border: 1px solid var(--tone-amber-bd); }
  .cfb-picker-item.tone-gray   & { background: var(--tone-gray-bg);   color: var(--tone-gray);   border: 1px solid var(--tone-gray-bd); }
}
.cfb-picker-item-text {
  flex: 1;
  min-width: 0;
}
.cfb-picker-item-label {
  display: block;
  font-size: 13px;
  color: var(--cfb-fg);
  font-weight: 500;
}
.cfb-picker-item-helper {
  display: block;
  font-size: 11px;
  color: var(--cfb-fg-3);
  margin-top: 1px;
}
.cfb-picker-item-arrow {
  font-size: 14px;
  color: transparent;
  transition: color .12s;
  flex: 0 0 auto;
}

/* ────────────────────────────────────────────────────────────────────
   Animations
   ──────────────────────────────────────────────────────────────────── */
@keyframes rowEnter {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes pillEnter {
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
}
@keyframes fadeSlide {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>
